import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, X, Play, Trash2,
  Check, Edit3,
} from 'lucide-react';
import { searchWorkers, getEventsByRange, createEvent, EVENT_TYPES, APPROVAL_STATUS } from '../services/api';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, dayKeyInZone, addDaysToDayKey, zonedDateTimeToInstant,
} from '../lib/workerTime';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

// Tokens del campo "Tipo de Fichaje" en los Excel de Intratime → eventType del backend.
//   Entrada  → 0 CLOCK_IN
//   Salida   → 1 CLOCK_OUT
//   Pausa    → 6 PAUSE_START
//   Regreso  → 7 PAUSE_END
const TOKEN_TO_EVENT = {
  'entrada': EVENT_TYPES.CLOCK_IN,
  'salida':  EVENT_TYPES.CLOCK_OUT,
  'pausa':   EVENT_TYPES.PAUSE_START,
  'regreso': EVENT_TYPES.PAUSE_END,
};

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// Limpia un NIF/DNI: solo letras y dígitos en mayúsculas.
const cleanNif = (s) => String(s || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

// "DD/MM/YYYY" o "D/M/YYYY" → "YYYY-MM-DD". Si no es parseable, null.
const parseDdMmYyyy = (s) => {
  const m = String(s || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return null;
  const dd = String(m[1]).padStart(2, '0');
  const mm = String(m[2]).padStart(2, '0');
  return `${m[3]}-${mm}-${dd}`;
};

// Combina "YYYY-MM-DD" + "HH:MM:SS" del Excel → ISO UTC, interpretando la
// fecha y la hora en la zona del TRABAJADOR (`tz`), nunca en la del
// navegador del admin. Al parsear aún no sabemos de quién es el fichero, así
// que se usa Madrid y cada evento guarda `localYmd`/`localHms` para
// recalcular el instante con la zona real del trabajador al importar.
const combineLocalIso = (ymd, hms, tz = DEFAULT_TIME_ZONE) => {
  if (!ymd || !hms) return null;
  const m = String(hms).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return null;
  const hh = String(m[1]).padStart(2, '0');
  const mm = m[2];
  const ss = m[3] || '00';
  const d = zonedDateTimeToInstant(ymd, `${hh}:${mm}:${ss}`, tz);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Instante del evento en la zona del trabajador destino (si conocemos la
// fecha/hora originales del Excel) o el calculado al parsear.
const eventTsForTz = (ev, tz) => (
  ev?.localYmd && ev?.localHms ? (combineLocalIso(ev.localYmd, ev.localHms, tz) || ev.timestamp) : ev?.timestamp
);

// Clave de deduplicado: tipo + instante en ms (independiente del formato
// del string que devuelva el backend).
const eventKey = (type, ts) => `${type}|${new Date(ts).getTime()}`;

// Algunos exports de Intratime traen una hoja "Worksheet" delante del
// listado real. A veces está vacía, a veces trae basura del template y
// pasa el filtro de "!ref" — y entonces el parser elige esa en lugar de
// la hoja del empleado, devolviendo "no se encontró la cabecera". Aquí
// elegimos la primera hoja que (a) tenga !ref y (b) no se llame Worksheet,
// Sheet1, Hoja1, etc. Como fallback final, devolvemos la primera con !ref
// sea cual sea su nombre, para no romper exports raros.
const SHEET_BLACKLIST = new Set(['worksheet', 'sheet1', 'sheet', 'hoja1', 'hoja']);

const pickDataSheet = (wb) => {
  const names = wb?.SheetNames || [];
  const isUsable = (name) => {
    const s = wb.Sheets[name];
    return !!(s && s['!ref']);
  };
  const goodName = (name) => !SHEET_BLACKLIST.has(String(name).trim().toLowerCase());
  for (const name of names) {
    if (isUsable(name) && goodName(name)) return wb.Sheets[name];
  }
  for (const name of names) {
    if (isUsable(name)) return wb.Sheets[name];
  }
  return wb.Sheets[names[0]] || null;
};

// Día (YYYY-MM-DD) de un ISO timestamp EN LA ZONA DEL TRABAJADOR (no la del
// navegador del admin que importa).
const dayOfIso = (iso, tz = DEFAULT_TIME_ZONE) => (iso ? dayKeyInZone(iso, tz) : null);

// Día de un evento importado: la fecha del Excel si la tenemos (ya es el día
// del trabajador); si no, el día del instante en su zona.
const eventDay = (ev, tz) => ev?.localYmd || dayOfIso(ev?.timestamp, tz);

// "Entrada >> Salida" o "Entrada  >>  Salida" → ['entrada','salida'].
const splitInterval = (s) => {
  if (!s) return [];
  return String(s)
    .split(/>>|→|->|»/)
    .map((p) => norm(p))
    .filter(Boolean);
};

// "07:48:34 >> 14:06:02" → ['07:48:34','14:06:02'].
const splitTimes = (s) => {
  if (!s) return [];
  return String(s)
    .split(/>>|→|->|»/)
    .map((p) => p.trim())
    .filter(Boolean);
};

// Núcleo de validación: dado el contenido textual de una fila (más una fecha
// de fallback heredada de la fila anterior si la celda fecha está vacía),
// produce los 2 eventos correspondientes o un mensaje de error concreto.
// Reusado por el parser inicial y por el flujo de "edición de avisos".
const tryBuildEventsFromRow = ({ dateText, intervalText, typeText, comment, fallbackDateYmd }) => {
  const dt = String(dateText || '').trim();
  let ymd = null;
  if (dt) {
    ymd = parseDdMmYyyy(dt);
    if (!ymd) return { error: `Fecha inválida: "${dt}". Formato esperado DD/MM/AAAA.` };
  } else if (fallbackDateYmd) {
    ymd = fallbackDateYmd;
  } else {
    return { error: 'Falta la fecha (no hay fila previa de la que heredarla).' };
  }
  const it = String(intervalText || '').trim();
  const tt = String(typeText || '').trim();
  if (!it || !tt) return { error: 'Faltan el intervalo horario o el tipo de fichaje.' };
  const times = splitTimes(it);
  const tokens = splitInterval(tt);
  if (times.length !== 2 || tokens.length !== 2) {
    return { error: `No se pudo interpretar "${it}" / "${tt}". Formato: "HH:MM:SS >> HH:MM:SS" y "Entrada >> Salida".` };
  }
  const t1 = TOKEN_TO_EVENT[tokens[0]];
  const t2 = TOKEN_TO_EVENT[tokens[1]];
  if (t1 == null || t2 == null) {
    return { error: `Tipos desconocidos: "${tokens[0]} / ${tokens[1]}". Valores válidos: Entrada, Salida, Pausa, Regreso.` };
  }
  const ts1 = combineLocalIso(ymd, times[0]);
  const ts2 = combineLocalIso(ymd, times[1]);
  if (!ts1 || !ts2) return { error: `Hora inválida en "${it}".` };
  return {
    events: [
      { eventType: t1, timestamp: ts1, comment: comment || '', localYmd: ymd, localHms: times[0] },
      { eventType: t2, timestamp: ts2, comment: comment || '', localYmd: ymd, localHms: times[1] },
    ],
  };
};

// Lee un Excel y devuelve { dni, name, events: [{date, time, type}], errors }.
// Tolera variaciones del export Intratime (cabeceras, encoding, columnas extra).
const parseIntratimeWorkbook = (buffer, filename) => {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheet = pickDataSheet(wb);
  if (!sheet) {
    return { error: `${filename}: hoja vacía o ilegible.` };
  }
  // raw=false para que devuelva strings con el formato visible.
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });

  // 1) Buscar DNI/NIF y nombre en las primeras 30 filas. Tolera tres formatos:
  //    (a) "DNI: 28805267K" todo en una celda
  //    (b) "DNI:" en una celda y "28805267K" en otra (export por columnas)
  //    (c) Igual para "Empleado: ..."
  // Para cubrir (b) concatenamos las celdas no vacías de la fila con espacio
  // antes de aplicar el regex.
  let dni = null;
  let workerName = null;
  for (let i = 0; i < Math.min(rows.length, 30); i += 1) {
    const cells = (rows[i] || []).map((c) => String(c || '').trim());
    const joined = cells.filter(Boolean).join(' ');
    if (!joined) continue;
    if (!dni) {
      const md = joined.match(/\b(?:DNI|NIF)\s*[:#]?\s*([0-9A-Za-z][0-9A-Za-z\s\-]{4,14}[0-9A-Za-z])/i);
      if (md) {
        const candidate = cleanNif(md[1]);
        if (candidate.length >= 6 && candidate.length <= 12) dni = candidate;
      }
    }
    if (!workerName) {
      // Empleado: ... hasta encontrar otro label conocido (DNI/NIF/Nº Afiliación)
      // o fin de la fila. Sin el lookahead, si "Empleado: Sergio Arroyo" y
      // "DNI: 28842820S" están en la misma fila concatenada, agarraríamos
      // el DNI dentro del nombre.
      //
      // Exigimos el separador ":" o "#" para no atrapar el título de la
      // primera fila del informe ("Informe por empleado DD-MM-YYYY..."),
      // que contiene la palabra "empleado" pero sin dos puntos.
      const mn = joined.match(/(?:^|\s)Empleado\s*[:#]\s*([^\s].*?)(?=\s+(?:DNI|NIF|N[º°ºo]\s*Afili)|$)/i);
      if (mn) workerName = mn[1].trim();
    }
    if (dni && workerName) break;
  }
  if (!dni) {
    return { error: `${filename}: no se encontró DNI/NIF en el archivo. Asegúrate de exportar el informe por empleado completo desde Intratime.` };
  }

  // 2) Localizar la fila de cabeceras del listado (busca "Fecha y Día" y "Intervalo horario").
  let headerRow = -1;
  let colDate = -1, colInterval = -1, colType = -1, colComment = -1;
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i].map(norm);
    if (r.some((c) => c.includes('fecha y dia')) && r.some((c) => c.includes('intervalo horario'))) {
      headerRow = i;
      colDate = r.findIndex((c) => c.includes('fecha y dia'));
      colInterval = r.findIndex((c) => c.includes('intervalo horario'));
      colType = r.findIndex((c) => c.includes('tipo de fichaje'));
      colComment = r.findIndex((c) => c === 'comentario');
      break;
    }
  }
  if (headerRow < 0) {
    return { error: `${filename}: no se encontró la cabecera "Fecha y Día / Intervalo horario / Tipo de Fichaje".` };
  }

  // 3) Recorrer filas de datos. Una fila Intratime tiene la forma
  //    "DD/MM/YYYY Lunes" | "07:48:34 >> 14:06:02" | "Entrada >> Salida" | comentario | ...
  //    La fecha sólo se imprime al cambiar de día; las filas siguientes del
  //    mismo día tienen la celda fecha vacía. Mantenemos la última vista.
  const events = [];
  // Cada error es ahora un objeto editable por la UI:
  //   { id, rowIndex, reason, dateText, intervalText, typeText, comment, fallbackDateYmd }
  const errors = [];
  let lastDate = null;
  let warningSeq = 0;
  for (let i = headerRow + 1; i < rows.length; i += 1) {
    const row = rows[i];
    if (!row || row.length === 0) continue;
    const dateCell = String(row[colDate] || '').trim();
    if (dateCell) {
      const d = parseDdMmYyyy(dateCell);
      if (d) lastDate = d;
    }
    const intervalCell = String(row[colInterval] || '').trim();
    const typeCell = String(row[colType] || '').trim();
    const commentCell = colComment >= 0 ? String(row[colComment] || '').trim() : '';
    if (!intervalCell || !typeCell) continue;
    if (norm(typeCell) === 'totales') break;

    const result = tryBuildEventsFromRow({
      dateText: dateCell,
      intervalText: intervalCell,
      typeText: typeCell,
      comment: commentCell,
      fallbackDateYmd: lastDate,
    });
    if (result.error) {
      warningSeq += 1;
      errors.push({
        id: `w-${i}-${warningSeq}`,
        rowIndex: i + 1,
        reason: `Fila ${i + 1}: ${result.error}`,
        dateText: dateCell,
        intervalText: intervalCell,
        typeText: typeCell,
        comment: commentCell,
        fallbackDateYmd: lastDate,
      });
      continue;
    }
    events.push(...result.events);
  }

  return { dni, workerName, events, errors };
};

// ─── Parser formato "Control Laboral" ─────────────────────────────────────
//
// Estructura del Excel:
//   - Cabeceras: Nombre · NIF · Puesto · Centro · Nombre Horario · Fecha ·
//     Corregido · Horario · Previsto · Registrado · Bolsa · Compensadas ·
//     Para Compensación · Estado · Comentario.
//   - Una fila por trabajador y día. El NIF se repite en cada fila.
//   - "Horario" trae las franjas del día separadas por salto de línea:
//       "De 07:51 - 12:21\nDe 12:38 - 15:08"
//     Cada franja = un CLOCK_IN (start) + un CLOCK_OUT (end). Si hay dos
//     franjas (turno partido) se generan 4 eventos.
//   - Ignoramos Bolsa, Compensadas, Para Compensación: el cómputo lo hace
//     TemControl. Solo necesitamos los fichajes reales del trabajador.
//
// Devuelve un ARRAY de entries (uno por trabajador encontrado), porque un
// único archivo Control Laboral puede listar a varios empleados.

const CL_HEADER_ALIASES = {
  nif:      ['nif', 'dni'],
  fecha:    ['fecha'],
  horario:  ['horario'],
  nombre:   ['nombre', 'empleado', 'trabajador'],
  comment:  ['comentario'],
};

const findColumn = (normalizedRow, aliases) => {
  for (const alias of aliases) {
    const idx = normalizedRow.findIndex((c) => c === alias);
    if (idx >= 0) return idx;
  }
  // Fallback con "incluye" (algunos exports añaden sufijos como "Fecha *")
  for (const alias of aliases) {
    const idx = normalizedRow.findIndex((c) => c.includes(alias));
    if (idx >= 0) return idx;
  }
  return -1;
};

// "De 07:51 - 12:21" → { start: "07:51:00", end: "12:21:00" }
// Tolera segundos opcionales, separadores con/sin espacio, y "de" en
// mayúsculas o sin acento.
const HORARIO_FRANJA_RE = /(?:de\s+)?(\d{1,2}:\d{2}(?::\d{2})?)\s*[-–—]\s*(\d{1,2}:\d{2}(?::\d{2})?)/gi;

const parseHorarioFranjas = (text) => {
  const out = [];
  if (!text) return out;
  const s = String(text);
  let m;
  HORARIO_FRANJA_RE.lastIndex = 0;
  while ((m = HORARIO_FRANJA_RE.exec(s)) !== null) {
    const ensureSeconds = (h) => (h.length === 5 ? `${h}:00` : h);
    const start = ensureSeconds(m[1]);
    const end = ensureSeconds(m[2]);
    // Descartamos franjas degeneradas "00:00 - 00:00" (se ven en filas
    // anuladas o con la jornada teórica vacía).
    if (start === '00:00:00' && end === '00:00:00') continue;
    out.push({ start, end });
  }
  return out;
};

const parseControlLaboralWorkbook = (buffer, filename) => {
  const wb = XLSX.read(buffer, { type: 'array', cellDates: false });
  const sheet = pickDataSheet(wb);
  if (!sheet) return { error: `${filename}: hoja vacía o ilegible.` };

  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });

  // Buscar cabecera. Aceptamos pequeñas variaciones en el orden de las
  // columnas pero exigimos al menos NIF, Fecha y Horario.
  let headerRow = -1;
  let colNif = -1, colFecha = -1, colHorario = -1, colNombre = -1, colComment = -1;
  for (let i = 0; i < Math.min(rows.length, 30); i += 1) {
    const r = (rows[i] || []).map(norm);
    const idxNif = findColumn(r, CL_HEADER_ALIASES.nif);
    const idxFecha = findColumn(r, CL_HEADER_ALIASES.fecha);
    const idxHorario = findColumn(r, CL_HEADER_ALIASES.horario);
    if (idxNif >= 0 && idxFecha >= 0 && idxHorario >= 0) {
      headerRow = i;
      colNif = idxNif;
      colFecha = idxFecha;
      colHorario = idxHorario;
      colNombre = findColumn(r, CL_HEADER_ALIASES.nombre);
      colComment = findColumn(r, CL_HEADER_ALIASES.comment);
      break;
    }
  }
  if (headerRow < 0) {
    return { error: `${filename}: no se encontró la cabecera (Nombre / NIF / Fecha / Horario).` };
  }

  // Agrupamos por NIF. Cada grupo se convertirá en una entry del modal.
  const byNif = new Map();
  const ensureEntry = (rawNif, rawName) => {
    const key = cleanNif(rawNif);
    if (!key) return null;
    if (!byNif.has(key)) {
      byNif.set(key, {
        dni: key,
        workerName: String(rawName || '').trim() || null,
        events: [],
        errors: [],
      });
    }
    return byNif.get(key);
  };

  let warningSeq = 0;
  for (let i = headerRow + 1; i < rows.length; i += 1) {
    const row = rows[i] || [];
    const nifCell = String(row[colNif] || '').trim();
    if (!nifCell) continue; // fila vacía / total
    const fechaCell = String(row[colFecha] || '').trim();
    const horarioCell = String(row[colHorario] || '').trim();
    const nameCell = colNombre >= 0 ? String(row[colNombre] || '').trim() : '';
    const commentCell = colComment >= 0 ? String(row[colComment] || '').trim() : '';

    const entry = ensureEntry(nifCell, nameCell);
    if (!entry) continue;

    const ymd = parseDdMmYyyy(fechaCell);
    if (!ymd) {
      warningSeq += 1;
      entry.errors.push({
        id: `cl-${i}-${warningSeq}`,
        rowIndex: i + 1,
        reason: `Fila ${i + 1}: fecha inválida ("${fechaCell}"). Formato esperado DD/MM/AAAA.`,
        dateText: fechaCell,
        intervalText: horarioCell,
        typeText: 'Entrada >> Salida',
        comment: commentCell,
        fallbackDateYmd: null,
      });
      continue;
    }

    const franjas = parseHorarioFranjas(horarioCell);
    if (franjas.length === 0) {
      // No hay franjas legibles: lo registramos como aviso editable.
      warningSeq += 1;
      entry.errors.push({
        id: `cl-${i}-${warningSeq}`,
        rowIndex: i + 1,
        reason: `Fila ${i + 1}: no se interpretaron franjas en "${horarioCell}". Formato esperado "De HH:MM - HH:MM".`,
        dateText: fechaCell,
        intervalText: horarioCell,
        typeText: 'Entrada >> Salida',
        comment: commentCell,
        fallbackDateYmd: ymd,
      });
      continue;
    }

    for (const f of franjas) {
      const ts1 = combineLocalIso(ymd, f.start);
      const ts2 = combineLocalIso(ymd, f.end);
      if (!ts1 || !ts2) {
        warningSeq += 1;
        entry.errors.push({
          id: `cl-${i}-${warningSeq}`,
          rowIndex: i + 1,
          reason: `Fila ${i + 1}: hora inválida en franja ${f.start}-${f.end}.`,
          dateText: fechaCell,
          intervalText: `${f.start} >> ${f.end}`,
          typeText: 'Entrada >> Salida',
          comment: commentCell,
          fallbackDateYmd: ymd,
        });
        continue;
      }
      entry.events.push({ eventType: EVENT_TYPES.CLOCK_IN, timestamp: ts1, comment: commentCell, localYmd: ymd, localHms: f.start });
      entry.events.push({ eventType: EVENT_TYPES.CLOCK_OUT, timestamp: ts2, comment: commentCell, localYmd: ymd, localHms: f.end });
    }
  }

  const workers = Array.from(byNif.values());
  if (workers.length === 0) {
    return { error: `${filename}: ningún NIF válido en el archivo.` };
  }
  return { workers };
};

// ─── UI ────────────────────────────────────────────────────────────────────

// Fila editable para un aviso. Los inputs muestran lo que vino del Excel y
// el usuario puede ajustarlos antes de pulsar "Añadir". El re-parseo se hace
// con la misma función que el parser original, así que el evento añadido es
// exactamente equivalente a uno proveniente de una fila bien formada.
const WarningRow = ({ warning, onChange, onAdd, onDiscard }) => (
  <li className="border border-warning/20 bg-warning/5 rounded-lg p-2.5 space-y-1.5">
    <div className="flex items-start gap-2">
      <AlertTriangle size={12} className="text-warning shrink-0 mt-0.5" />
      <div className="flex-1 text-[11px] text-ink-muted leading-snug">{warning.reason}</div>
    </div>
    <div className="grid grid-cols-12 gap-1.5">
      <input
        type="text"
        value={warning.dateText}
        onChange={(e) => onChange({ dateText: e.target.value })}
        placeholder="DD/MM/AAAA"
        className="col-span-3 px-2 py-1 border border-slate-200 rounded text-[11px] font-mono focus:outline-none focus:border-primary"
        title="Fecha (formato DD/MM/AAAA). Si la dejas vacía y la fila anterior tenía fecha, se hereda."
      />
      <input
        type="text"
        value={warning.intervalText}
        onChange={(e) => onChange({ intervalText: e.target.value })}
        placeholder="07:00:00 >> 15:00:00"
        className="col-span-4 px-2 py-1 border border-slate-200 rounded text-[11px] font-mono focus:outline-none focus:border-primary"
        title="Intervalo horario, formato HH:MM:SS >> HH:MM:SS."
      />
      <input
        type="text"
        value={warning.typeText}
        onChange={(e) => onChange({ typeText: e.target.value })}
        placeholder="Entrada >> Salida"
        className="col-span-3 px-2 py-1 border border-slate-200 rounded text-[11px] focus:outline-none focus:border-primary"
        title="Tipo de fichaje. Valores válidos: Entrada, Salida, Pausa, Regreso."
      />
      <div className="col-span-2 flex gap-1">
        <button
          type="button"
          onClick={onAdd}
          className="flex-1 inline-flex items-center justify-center gap-1 px-1.5 py-1 rounded text-[10px] font-semibold bg-success text-white hover:bg-emerald-700 transition-colors"
          title="Validar y añadir como evento"
        >
          <Check size={11} />
        </button>
        <button
          type="button"
          onClick={onDiscard}
          className="flex-1 inline-flex items-center justify-center gap-1 px-1.5 py-1 rounded text-[10px] font-semibold bg-slate-200 text-ink-muted hover:bg-slate-300 transition-colors"
          title="Descartar este aviso"
        >
          <X size={11} />
        </button>
      </div>
    </div>
  </li>
);

const FileRow = ({ entry, onRemove, onWarningChange, onWarningAdd, onWarningDiscard, onToggleSkipConflicts }) => (
  <li className="flex items-start gap-3 py-2 border-t border-slate-100">
    <FileSpreadsheet size={16} className={`shrink-0 mt-0.5 ${entry.status === 'error' ? 'text-danger' : entry.status === 'done' ? 'text-success' : 'text-primary'}`} />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-ink truncate">{entry.filename}</div>
      <div className="text-[11px] text-ink-muted mt-0.5">
        {entry.status === 'pending' && (entry.workerName ? `${entry.workerName} (${entry.dni}) · ${entry.events?.length || 0} eventos` : 'Pendiente de análisis')}
        {entry.status === 'parsing' && 'Analizando…'}
        {entry.status === 'parsed' && (entry.workerName ? `${entry.workerName} (${entry.dni}) · ${entry.events?.length || 0} eventos${entry.errors?.length ? `, ${entry.errors.length} avisos` : ''}` : `${entry.events?.length || 0} eventos`)}
        {entry.status === 'uploading' && `Subiendo… ${entry.uploaded || 0}/${entry.events?.length || 0}`}
        {entry.status === 'done' && `${entry.uploaded || 0} eventos creados${entry.skipped ? `, ${entry.skipped} omitidos` : ''}${entry.failed ? `, ${entry.failed} con error` : ''}`}
        {entry.status === 'error' && (entry.error || 'Error')}
      </div>
      {entry.status === 'parsed' && entry.conflictDays?.length > 0 && (
        <div className="mt-1.5 rounded-lg border border-warning/30 bg-warning/5 p-2 text-[11px]">
          <div className="flex items-start gap-1.5">
            <AlertTriangle size={11} className="text-warning shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-warning">
                {entry.conflictDays.length === 1
                  ? '1 día ya tiene fichajes para este trabajador'
                  : `${entry.conflictDays.length} días ya tienen fichajes para este trabajador`}
              </div>
              <div className="text-ink-muted mt-0.5 break-words">
                {entry.conflictDays.slice(0, 12).join(' · ')}
                {entry.conflictDays.length > 12 && ` … y ${entry.conflictDays.length - 12} más`}
              </div>
            </div>
          </div>
          <label className="mt-1.5 inline-flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={entry.skipConflictDays !== false}
              onChange={() => onToggleSkipConflicts(entry.id)}
              className="w-3 h-3"
            />
            <span className="text-ink-muted">
              Omitir esos días en la importación
              <span className="text-ink-muted/70"> (recomendado para no duplicar fichajes existentes)</span>
            </span>
          </label>
        </div>
      )}
      {entry.errors?.length > 0 && entry.status !== 'error' && (
        <details className="mt-1" open>
          <summary className="text-[10px] text-warning cursor-pointer inline-flex items-center gap-1">
            <Edit3 size={10} /> {entry.errors.length} {entry.errors.length === 1 ? 'aviso editable' : 'avisos editables'}
          </summary>
          <ul className="mt-1.5 space-y-1.5">
            {entry.errors.map((w) => (
              <WarningRow
                key={w.id}
                warning={w}
                onChange={(patch) => onWarningChange(entry.id, w.id, patch)}
                onAdd={() => onWarningAdd(entry.id, w.id)}
                onDiscard={() => onWarningDiscard(entry.id, w.id)}
              />
            ))}
          </ul>
        </details>
      )}
    </div>
    {entry.status !== 'uploading' && (
      <button onClick={() => onRemove(entry.id)} className="p-1 rounded text-ink-muted hover:text-danger hover:bg-slate-100" title="Quitar">
        <Trash2 size={13} />
      </button>
    )}
  </li>
);

const BulkEventImportModal = ({ open, companyId, onClose, onDone }) => {
  const [files, setFiles] = useState([]); // [{id, filename, status, dni, workerName, events, errors, uploaded, skipped, error}]
  const [running, setRunning] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [workersIndex, setWorkersIndex] = useState(null);
  const toast = useToast();
  // Tipo de Excel a importar:
  //   'intratime'      → un archivo = un trabajador (formato actual)
  //   'controlLaboral' → un archivo = múltiples trabajadores (1 fila por
  //                       trabajador y día, franjas en el campo "Horario")
  const [importType, setImportType] = useState('intratime');
  const fileInputRef = useRef(null);

  const reset = () => {
    setFiles([]);
    setRunning(false);
    setGlobalError(null);
  };

  const close = () => {
    if (running) return;
    reset();
    onClose();
  };

  const buildWorkersIndexIfNeeded = async () => {
    if (workersIndex) return workersIndex;
    // Indexamos por DNI (limpio + uppercase). Pedimos solo los de esta empresa
    // pero también admitimos buscar por DNI globalmente como fallback.
    const list = await searchWorkers('');
    const idx = new Map();
    for (const w of (Array.isArray(list) ? list : [])) {
      if (!w?.nif) continue;
      const key = cleanNif(w.nif);
      if (key) idx.set(key, w);
    }
    setWorkersIndex(idx);
    return idx;
  };

  const handleFiles = async (fileList) => {
    setGlobalError(null);
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    const newEntries = arr.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      filename: f.name,
      status: 'parsing',
    }));
    setFiles((prev) => [...prev, ...newEntries]);

    // Parseo en paralelo. IntraTime devuelve 1 trabajador por archivo;
    // Control Laboral devuelve N — emitimos una entry por trabajador.
    const results = await Promise.all(newEntries.map(async (entry) => {
      try {
        const buffer = await entry.file.arrayBuffer();
        if (importType === 'controlLaboral') {
          const parsed = parseControlLaboralWorkbook(buffer, entry.filename);
          if (parsed.error) return { id: entry.id, status: 'error', error: parsed.error };
          return {
            id: entry.id,
            splitInto: parsed.workers.map((w, i) => ({
              id: `${entry.id}-${i}`,
              filename: w.workerName ? `${entry.filename} · ${w.workerName}` : entry.filename,
              status: 'parsed',
              dni: w.dni,
              workerName: w.workerName,
              events: w.events,
              errors: w.errors,
            })),
          };
        }
        const parsed = parseIntratimeWorkbook(buffer, entry.filename);
        if (parsed.error) return { id: entry.id, status: 'error', error: parsed.error };
        return {
          id: entry.id,
          status: 'parsed',
          dni: parsed.dni,
          workerName: parsed.workerName,
          events: parsed.events,
          errors: parsed.errors,
        };
      } catch (err) {
        return { id: entry.id, status: 'error', error: err.message || 'No se pudo leer el archivo.' };
      }
    }));

    let mergedFiles = null;
    setFiles((prev) => {
      const out = [];
      for (const f of prev) {
        const r = results.find((x) => x.id === f.id);
        if (!r) { out.push(f); continue; }
        if (r.splitInto) {
          // Reemplazamos la entry-archivo por N entries-trabajador.
          out.push(...r.splitInto);
        } else {
          out.push({ ...f, ...r });
        }
      }
      mergedFiles = out;
      return out;
    });

    // Tras parsear, chequeamos contra el backend si los días que vamos a
    // importar ya tienen fichajes para ese trabajador. Esto detecta dobles
    // importaciones (p.ej. el admin sube dos veces el mismo informe) y
    // permite al admin decidir si omitir esos días.
    if (mergedFiles) {
      const parsedEntries = mergedFiles.filter((f) => f.status === 'parsed');
      if (parsedEntries.length > 0) await runConflictChecks(parsedEntries);
    }
  };

  // Para cada entry parsed, busca en el backend los eventos del worker
  // en el rango de fechas que cubre el archivo y marca:
  //   conflictDays:  Set<YYYY-MM-DD>      → días que ya tienen fichajes
  //   existingKeys:  Set<"type|iso">       → eventos exactamente iguales
  //                                          (se saltan SIEMPRE para no
  //                                          crear duplicados, aunque el
  //                                          admin decida importar conflictos)
  //   skipConflictDays: boolean (default true) → toggle de la UI
  const runConflictChecks = async (entries) => {
    const idx = await buildWorkersIndexIfNeeded().catch(() => null);
    if (!idx) return;
    // En serie por trabajador para no machacar el backend ni el rate limit
    // del admin: la verificación normalmente es 5-30 trabajadores como
    // mucho en un import puntual.
    for (const entry of entries) {
      const worker = idx.get(cleanNif(entry.dni));
      if (!worker) {
        // Si no hay worker no podemos chequear; lo marcamos para que el
        // upload muestre el error.
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, conflictDays: [], existingKeys: [], skipConflictDays: true } : f));
        continue;
      }
      // Días del trabajador (zona Canarias / península del trabajador destino).
      const tz = timeZoneForWorker(worker);
      const days = Array.from(new Set((entry.events || []).map((e) => eventDay(e, tz)).filter(Boolean))).sort();
      if (days.length === 0) {
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, conflictDays: [], existingKeys: [], skipConflictDays: true } : f));
        continue;
      }
      // [primer día - 1, último día + 1]: el día de Madrid empieza a las
      // 22:00/23:00 UTC del anterior, y el backend antiguo era exclusivo en el
      // extremo final.
      const fromIso = addDaysToDayKey(days[0], -1);
      const toIso = addDaysToDayKey(days[days.length - 1], 1);
      let existing = [];
      try {
        const data = await getEventsByRange(worker.id, fromIso, toIso);
        existing = Array.isArray(data) ? data : [];
      } catch {
        // Fallo de red: dejamos sin chequeo, el admin decide. Mejor no
        // bloquear que falsos negativos.
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, conflictDays: [], existingKeys: [], skipConflictDays: true } : f));
        continue;
      }
      const conflictDays = new Set();
      const existingKeys = new Set();
      for (const ev of existing) {
        const d = dayOfIso(ev.timestamp, tz);
        if (d && days.includes(d)) conflictDays.add(d);
        existingKeys.add(eventKey(ev.eventType, ev.timestamp));
      }
      setFiles((prev) => prev.map((f) => f.id === entry.id ? {
        ...f,
        conflictDays: Array.from(conflictDays).sort(),
        existingKeys: Array.from(existingKeys),
        skipConflictDays: true,
      } : f));
    }
  };

  const toggleSkipConflicts = (id) => {
    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, skipConflictDays: !f.skipConflictDays } : f));
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((f) => f.id !== id));

  // Edita un campo de un aviso (fecha / intervalo / tipo / comentario). El
  // re-validado solo ocurre al pulsar "añadir"; mientras tanto, los inputs
  // muestran los textos crudos sin tocar la lista de events.
  const handleWarningChange = (fileId, warningId, patch) => {
    setFiles((prev) => prev.map((f) => {
      if (f.id !== fileId) return f;
      return {
        ...f,
        errors: (f.errors || []).map((w) => w.id === warningId ? { ...w, ...patch } : w),
      };
    }));
  };

  // Re-valida un aviso editado. Si pasa, lo mueve a events y lo quita de
  // errors. Si no, actualiza la razón del aviso con el nuevo motivo de error.
  const handleWarningAdd = (fileId, warningId) => {
    setFiles((prev) => prev.map((f) => {
      if (f.id !== fileId) return f;
      const w = (f.errors || []).find((x) => x.id === warningId);
      if (!w) return f;
      const result = tryBuildEventsFromRow({
        dateText: w.dateText,
        intervalText: w.intervalText,
        typeText: w.typeText,
        comment: w.comment,
        fallbackDateYmd: w.fallbackDateYmd,
      });
      if (result.error) {
        return {
          ...f,
          errors: f.errors.map((x) => x.id === warningId ? { ...x, reason: `Fila ${x.rowIndex}: ${result.error}` } : x),
        };
      }
      return {
        ...f,
        events: [...(f.events || []), ...result.events],
        errors: f.errors.filter((x) => x.id !== warningId),
      };
    }));
  };

  const handleWarningDiscard = (fileId, warningId) => {
    setFiles((prev) => prev.map((f) => {
      if (f.id !== fileId) return f;
      return { ...f, errors: (f.errors || []).filter((x) => x.id !== warningId) };
    }));
  };

  const runImport = async () => {
    setGlobalError(null);
    setRunning(true);
    // Totales para UN único toast de resumen al final (nunca uno por evento).
    const totals = { created: 0, skipped: 0, failed: 0, filesFailed: 0 };
    let aborted = null;
    try {
      const idx = await buildWorkersIndexIfNeeded();
      // Procesar en serie por archivo, eventos en serie también para no
      // saturar el backend con 100 requests paralelos.
      for (const entry of files) {
        if (entry.status !== 'parsed') continue;
        const worker = idx.get(cleanNif(entry.dni));
        if (!worker) {
          totals.filesFailed += 1;
          setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: 'error', error: `No hay ningún trabajador con DNI ${entry.dni} en el sistema.` } : f));
          continue;
        }
        // Si el companyId del modal no es null, validar que el worker pertenece a esa empresa.
        if (companyId != null && Number(worker.companyId) !== Number(companyId)) {
          totals.filesFailed += 1;
          setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: 'error', error: `El trabajador con DNI ${entry.dni} no pertenece a esta empresa.` } : f));
          continue;
        }
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: 'uploading', uploaded: 0, skipped: 0, failed: 0 } : f));
        let uploaded = 0;
        let skipped = 0;
        // Fallos REALES del servidor, separados de los omitidos a propósito
        // (duplicados / días con fichajes): antes se sumaban como "omitidos".
        let failed = 0;
        const conflictDaysSet = new Set(entry.conflictDays || []);
        const existingKeysSet = new Set(entry.existingKeys || []);
        const skipConflicts = entry.skipConflictDays !== false;
        // Fecha y hora del Excel = hora de reloj del TRABAJADOR destino.
        const workerTz = timeZoneForWorker(worker);
        for (const ev of entry.events) {
          const ts = eventTsForTz(ev, workerTz);
          // 1) Día con fichajes existentes y el admin ha optado por omitir
          //    → saltamos el evento.
          if (skipConflicts && conflictDaysSet.has(eventDay(ev, workerTz))) {
            skipped += 1;
            continue;
          }
          // 2) Evento exactamente igual (mismo type + instante) ya está
          //    en BD → SIEMPRE saltar, incluso si el admin pidió importar
          //    los conflictos. Esto evita duplicados literales.
          if (existingKeysSet.has(eventKey(ev.eventType, ts))) {
            skipped += 1;
            continue;
          }
          try {
            await createEvent({
              workerId: worker.id,
              eventType: ev.eventType,
              timestamp: ts,
              geoInfo: null,
              approvalStatus: APPROVAL_STATUS.APPROVED,
            });
            uploaded += 1;
          } catch {
            failed += 1;
          }
          // Actualización progresiva del contador (cada 5 eventos para no spamear).
          if (uploaded % 5 === 0 || skipped % 5 === 0) {
            setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, uploaded, skipped, failed } : f));
          }
        }
        totals.created += uploaded;
        totals.skipped += skipped;
        totals.failed += failed;
        setFiles((prev) => prev.map((f) => f.id === entry.id ? { ...f, status: 'done', uploaded, skipped, failed } : f));
      }
    } catch (err) {
      aborted = err;
      setGlobalError(err.message || 'Error durante la importación.');
    } finally {
      setRunning(false);
      // Cortada sin importar nada: el banner del modal (que sigue abierto)
      // ya explica el error; no lo duplicamos. En otro caso, resumen.
      if (!(aborted && totals.created === 0)) {
        const summary = FEEDBACK.eventsImported({
          created: totals.created,
          skipped: totals.skipped,
          failed: totals.failed,
          filesFailed: totals.filesFailed,
        });
        toast.show(aborted ? 'warning' : summary.type, summary.message);
      }
      if (typeof onDone === 'function') onDone();
    }
  };

  if (!open) return null;

  const ready = files.some((f) => f.status === 'parsed');
  const allDone = files.length > 0 && files.every((f) => f.status === 'done' || f.status === 'error');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl my-8 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Empresa #{companyId}</div>
            <h2 className="font-display text-lg font-bold text-ink">Importar fichajes desde Excel</h2>
          </div>
          <button onClick={close} disabled={running} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2">Tipo de archivo</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setImportType('intratime')}
                disabled={running || files.length > 0}
                className={`text-left p-3 rounded-xl border transition-colors ${
                  importType === 'intratime'
                    ? 'border-primary bg-primary-soft/50'
                    : 'border-slate-200 hover:bg-slate-50'
                } disabled:opacity-60 disabled:cursor-not-allowed`}
                title={files.length > 0 ? 'Quita los archivos cargados para cambiar el tipo' : ''}
              >
                <div className={`text-sm font-semibold ${importType === 'intratime' ? 'text-primary-darker' : 'text-ink'}`}>IntraTime</div>
                <div className="text-[11px] text-ink-muted mt-0.5">Un archivo por trabajador. Cabecera con DNI/Empleado y filas Fecha · Intervalo · Tipo de Fichaje.</div>
              </button>
              <button
                type="button"
                onClick={() => setImportType('controlLaboral')}
                disabled={running || files.length > 0}
                className={`text-left p-3 rounded-xl border transition-colors ${
                  importType === 'controlLaboral'
                    ? 'border-primary bg-primary-soft/50'
                    : 'border-slate-200 hover:bg-slate-50'
                } disabled:opacity-60 disabled:cursor-not-allowed`}
                title={files.length > 0 ? 'Quita los archivos cargados para cambiar el tipo' : ''}
              >
                <div className={`text-sm font-semibold ${importType === 'controlLaboral' ? 'text-primary-darker' : 'text-ink'}`}>Control Laboral</div>
                <div className="text-[11px] text-ink-muted mt-0.5">Un archivo con varios trabajadores. Cada fila es un día y las franjas vienen en "Horario".</div>
              </button>
            </div>
          </div>

          <div className="text-xs text-ink bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
            {importType === 'intratime' ? (
              <>Sube uno o varios <strong>informes por empleado</strong> exportados de Intratime (.xlsx).
              El sistema localiza el DNI dentro del archivo y crea los eventos de entrada, salida y pausa
              asociados al trabajador correspondiente.</>
            ) : (
              <>Sube uno o varios Excel del formato <strong>Control Laboral</strong> con columnas
              Nombre · NIF · Fecha · Horario · etc. El sistema lee las franjas de "Horario"
              (p.ej. "De 07:51 - 12:21") y crea entradas y salidas por cada una.
              Las columnas <strong>Bolsa</strong>, <strong>Compensadas</strong> y <strong>Para Compensación</strong>
              se ignoran — esos cálculos los hace TemControl automáticamente.</>
            )}
          </div>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              multiple
              className="hidden"
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={running}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-primary-darker border-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary-soft transition-colors disabled:opacity-50"
            >
              <Upload size={16} /> Seleccionar archivos .xlsx (puedes elegir varios)
            </button>
          </div>

          {files.length > 0 && (
            <ul>
              {files.map((entry) => (
                <FileRow
                  key={entry.id}
                  entry={entry}
                  onRemove={removeFile}
                  onWarningChange={handleWarningChange}
                  onWarningAdd={handleWarningAdd}
                  onWarningDiscard={handleWarningDiscard}
                  onToggleSkipConflicts={toggleSkipConflicts}
                />
              ))}
            </ul>
          )}

          {globalError && (
            <div className="text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{globalError}</span>
            </div>
          )}

          {allDone && (
            <div className="text-xs text-success bg-success/5 border border-success/30 rounded-lg px-3 py-2 flex items-start gap-2">
              <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
              <span>Importación finalizada. Cierra el modal y refresca el equipo para verlos.</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 shrink-0">
          <button onClick={close} disabled={running} className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 disabled:opacity-50">
            {allDone ? 'Cerrar' : 'Cancelar'}
          </button>
          <button
            onClick={runImport}
            disabled={!ready || running || allDone}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-50"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {running ? 'Importando…' : `Importar ${files.filter((f) => f.status === 'parsed').reduce((s, f) => s + (f.events?.length || 0), 0)} eventos`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEventImportModal;
